import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { useImportedInvoiceDrawer } from '@features/importedInvoices/context/importedInvoiceDrawer'
import { useImportedInvoiceReviewFeature } from '@features/importedInvoices/hooks/useImportedInvoiceReviewFeature'

import { Paging } from '../../../../../types/metable'
import { useImportedInvoicesList } from '../../../hooks/useImportedInvoicesList'
import { ImportedInvoicesTableRow } from '../../../types/importedInvoicesTableRow'
import { useImportedInvoicesListFilters } from './importedInvoicesListFilters'

interface ContextState {
  invoices: ImportedInvoicesTableRow[]
  isLoading: boolean
  isReviewEnabled: boolean
  onSelectRow: (invoice: ImportedInvoicesTableRow) => void
  pagination?: Paging
}

const ImportedInvoicesListDataContext = createContext<ContextState | undefined>(undefined)

interface ImportedInvoicesListDataProviderProps {
  children?: ReactNode
}

export const ImportedInvoicesListDataProvider = ({ children }: ImportedInvoicesListDataProviderProps): ReactElement => {
  const [{ page, pageSize, period, searchQuery, sortDirection, sortProperty }] = useImportedInvoicesListFilters()

  const { isEnabled: isReviewEnabled } = useImportedInvoiceReviewFeature()
  const { openDrawer } = useImportedInvoiceDrawer()

  const { invoices, isLoading, pagination } = useImportedInvoicesList({
    page,
    pageSize,
    period,
    searchQuery,
    sortDirection,
    sortProperty,
  })

  // Not gated on `isReviewEnabled`: the table withholds the handler entirely while the drawer is
  // disabled, which is also what stops the DS table rendering clickable rows.
  const handleRowSelect = useCallback(
    (invoice: ImportedInvoicesTableRow) => {
      openDrawer(invoice.id)
    },
    [openDrawer],
  )

  const value = useMemo(
    (): ContextState => ({
      invoices,
      isLoading,
      isReviewEnabled,
      onSelectRow: handleRowSelect,
      pagination,
    }),
    [handleRowSelect, invoices, isLoading, isReviewEnabled, pagination],
  )

  return <ImportedInvoicesListDataContext.Provider value={value}>{children}</ImportedInvoicesListDataContext.Provider>
}

export const useImportedInvoicesListData = (): ContextState => {
  const context = useContext(ImportedInvoicesListDataContext)

  if (!context) {
    throw new Error('ImportedInvoicesListDataContext is missing in the module!')
  }

  return context
}
